"use client";

import { LoginFormContent } from "@/components/login-dialog/form";
import { PostLoginChoiceDialog } from "@/components/post-login-choice-dialog";
import { Button } from "@/components/rac/button";
import { DialogContent, DialogOverlay, DialogTrigger } from "@/components/rac/dialog";
import { Sheet, SheetContent, SheetOverlay } from "@/components/rac/sheet";
import { useLoginForm } from "@/hooks/use-login-form";
import { useMediaQuery } from "@/hooks/use-media-query";
import { X } from "lucide-react";
import Image from "next/image";
import type { ReactNode } from "react";
import { createContext, useEffect, useState } from "react";

interface LoginDialogOptions {
	onLoginSuccess?: () => void;
	isShort?: boolean;
}

export interface LoginDialogContextValue {
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	openDialog: (options?: LoginDialogOptions) => void;
	closeDialog: () => void;
}

export const LoginDialogContext = createContext<LoginDialogContextValue | undefined>(undefined);

export interface LoginDialogProviderProps {
	children: ReactNode;
}

export function LoginDialogProvider({ children }: LoginDialogProviderProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [dialogOptions, setDialogOptions] = useState<LoginDialogOptions>({});
	const isMobile = useMediaQuery("(max-width: 768px)");
	const formProps = useLoginForm({
		onLoginSuccess: dialogOptions.onLoginSuccess,
		setIsOpen,
		isShort: dialogOptions.isShort,
	});

	const openDialog = (options: LoginDialogOptions = {}) => {
		setDialogOptions(options);
		setIsOpen(true);
	};

	const closeDialog = () => setIsOpen(false);

	// Reset form when dialog closes (but not for short forms)
	// Add delay to ensure dialog close animation completes before resetting
	useEffect(() => {
		if (!isOpen && !dialogOptions.isShort) {
			const timer = setTimeout(() => {
				formProps.resetForm();
			}, 300); // Wait for dialog close animation to complete
			return () => clearTimeout(timer);
		}
	}, [isOpen, formProps, dialogOptions.isShort]);

	const value: LoginDialogContextValue = {
		isOpen,
		setIsOpen,
		openDialog,
		closeDialog,
	};

	const renderDialog = () => {
		if (!isOpen) return null;

		// short login form
		if (dialogOptions.isShort) return <LoginFormContent {...formProps} isMobile={isMobile} />;

		// Mobile: Bottom Sheet
		if (isMobile) {
			return (
				<Sheet isOpen={isOpen} onOpenChange={setIsOpen}>
					<SheetOverlay className="z-10000!">
						<SheetContent
							side="bottom"
							className="h-auto max-h-[90dvh] rounded-t-2xl bg-gradient-to-br from-orange-100 via-red-50 to-pink-50"
							closeButton={false}
						>
							{/* Floating close button positioned outside the SheetContent */}
							<div className="relative">
								<Button
									variant="bareshell"
									onClick={() => setIsOpen(false)}
									className="absolute -top-14 right-4 p-2 z-1 rounded-full bg-gray-100 hover:bg-gray-200 shadow-lg transition-colors"
									aria-label="Close dialog"
								>
									<X className="h-6 w-6 text-gray-800" />
								</Button>
							</div>

							<div className="flex-1 overflow-y-auto">
								<LoginFormContent {...formProps} isMobile={true} />
							</div>
						</SheetContent>
					</SheetOverlay>
				</Sheet>
			);
		}

		// Desktop: Regular Dialog
		return (
			<DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
				<DialogOverlay className="bg-black/50 z-10000!">
					<div className="relative max-w-4xl">
						{/* Floating close button positioned relative to dialog content */}

						<DialogContent
							className="max-w-4xl w-full h-[600px] md:h-[600px] sm:h-full sm:max-h-[90dvh] bg-transparent border-0 shadow-none p-0"
							closeButton={false}
						>
							<Button
								variant="bareshell"
								onClick={() => setIsOpen(false)}
								className="absolute -right-4 -top-4 p-2 z-1 rounded-full bg-gray-100 hover:bg-gray-200 shadow-lg transition-colors"
								aria-label="Close dialog"
							>
								<X className="h-6 w-6 text-gray-800" />
							</Button>
							<div className="p-0 overflow-hidden bg-white border-0 shadow-2xl sm:rounded-2xl rounded-none mb-8">
								<div className="flex flex-col md:flex-row h-full" data-testid="login-dialog" id="login-dialog">
									{/* Left side - Illustration with Gradient */}
									<div className="md:w-1/2 bg-gradient-to-br from-red-500 via-red-400 to-red-600 relative overflow-hidden">
										{/* Background pattern */}
										<div className="absolute inset-0 opacity-20">
											<div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/10 to-transparent"></div>
										</div>

										{/* Logo and content */}
										<div className="flex flex-col items-center justify-center h-full p-6 md:p-8">
											<div className="text-center max-w-sm">
												<div className="relative h-40 mb-6">
													<Image
														src="/images/login/unlock.svg"
														alt="Login illustration"
														fill
														className="object-contain"
													/>
												</div>

												<h3 className="text-lg font-semibold text-white mb-2">Welcome to Settlin</h3>
												<p className="text-sm text-white/90">
													Unlock your perfect home with our personalized property recommendations
												</p>
											</div>
										</div>
									</div>

									{/* Right side - Form */}
									<div className="flex-1 bg-gradient-to-br from-orange-100 via-red-50 to-pink-50 flex flex-col justify-center px-8 py-8 relative">
										<LoginFormContent {...formProps} />
									</div>
								</div>
							</div>
						</DialogContent>
					</div>
				</DialogOverlay>
			</DialogTrigger>
		);
	};

	return (
		<LoginDialogContext.Provider value={value}>
			{children}
			{renderDialog()}
			{/* Post-Login Choice Dialog - shown after successful login */}
			<PostLoginChoiceDialog
				isOpen={formProps.showRelationshipGuard}
				onClose={() => formProps.setShowRelationshipGuard(false)}
				currentRelationship={
					formProps.currentUser?.relation === "buyer" || formProps.currentUser?.relation === "seller"
						? formProps.currentUser.relation
						: "buyer"
				}
				featureName="Choose Your Role"
				description="Tell us how you'd like to use Settlin today"
				onComplete={formProps.onRelationshipSelected}
			/>
		</LoginDialogContext.Provider>
	);
}
